// TwoFactorCard — Jun 2026 security hardening.
//
// Account → Security card for opt-in TOTP two-factor auth. Mirrors the local
// Card look used elsewhere in AccountSettings. Three states:
//   • OFF   → "Enable" button → setup (QR + secret + code) → enable → recovery codes
//   • ON    → status + "Turn off" (asks for a code)
//
// Talks to: GET/POST /api/auth/2fa/{status,setup,enable,disable}.
import { useEffect, useState } from "react";
import { ShieldCheck, CheckCircle, Copy, PauseCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser } from "@/lib/api";

function Card({ title, subtitle, children, testid }) {
  return (
    <div className="bg-white border hairline rounded-xl p-6 shadow-sm" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <ShieldCheck size={20} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-serif-display text-xl text-[#1C1B1A]">{title}</h3>
          {subtitle && <p className="text-sm text-[#6B6A68] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

export default function TwoFactorCard() {
  const [enabled, setEnabled] = useState(false);
  const [paused, setPaused] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [setup, setSetup] = useState(null);     // {qr_data_url, secret}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(null); // shown once after enable
  const [disarmCode, setDisarmCode] = useState("");
  const [showDisarm, setShowDisarm] = useState(false);

  const reload = async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setEnabled(!!r.data.enabled);
      setPaused(!!r.data.paused);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { reload(); }, []);

  const togglePause = async (next) => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/pause", { paused: next });
      setPaused(next);
      toast.success(next ? "Two-factor paused — logins skip the code for now." : "Two-factor resumed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update.");
    } finally { setBusy(false); }
  };

  const syncUserFlag = (val) => {
    const u = getUser();
    if (u) setUser({ ...u, has_2fa: val });
  };

  const beginSetup = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/setup");
      setSetup(r.data);
      setRecovery(null);
      setCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start setup.");
    } finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    setBusy(true);
    try {
      const r = await api.post("/auth/2fa/enable", { code });
      setRecovery(r.data.recovery_codes || []);
      setSetup(null);
      setEnabled(true);
      syncUserFlag(true);
      toast.success("Two-factor is on. Save your recovery codes.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "That code didn't match.");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disarmCode });
      setEnabled(false);
      setShowDisarm(false);
      setDisarmCode("");
      syncUserFlag(false);
      toast.success("Two-factor turned off.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enter a valid code to turn it off.");
    } finally { setBusy(false); }
  };

  const copyRecovery = () => {
    navigator.clipboard?.writeText((recovery || []).join("\n"));
    toast.success("Recovery codes copied.");
  };

  if (loading) {
    return <Card title="Two-factor authentication" subtitle="Loading…" testid="twofa-card" />;
  }

  return (
    <Card
      title="Two-factor authentication"
      subtitle="Add a one-time code from an authenticator app on top of your password. Strongly recommended for admin accounts."
      testid="twofa-card"
    >
      {/* Recovery codes (shown once right after enabling) */}
      {recovery && (
        <div className="rounded-lg border-2 border-[#9BB99F] bg-[#F0F7F1] p-4 mb-4" data-testid="twofa-recovery">
          <div className="text-sm text-[#2F6B3A] flex items-center gap-1.5 mb-2">
            <CheckCircle size={16} weight="duotone" /> Save these recovery codes somewhere safe.
          </div>
          <p className="text-xs text-[#6B6A68] mb-3">Each works once if you lose your phone. We won't show them again.</p>
          <div className="grid grid-cols-2 gap-1.5 font-mono text-sm">
            {recovery.map((c) => <div key={c} className="bg-white border hairline rounded px-2 py-1 text-center">{c}</div>)}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={copyRecovery} data-testid="twofa-copy-recovery"
              className="text-xs px-3 py-1.5 rounded border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center gap-1.5">
              <Copy size={12} /> Copy
            </button>
            <button onClick={() => setRecovery(null)} data-testid="twofa-recovery-done"
              className="text-xs px-3 py-1.5 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E]">
              I've saved them
            </button>
          </div>
        </div>
      )}

      {/* ON state */}
      {enabled && !recovery && (
        <div className="space-y-3">
          {paused ? (
            <div className="text-sm text-[#8A6D1F] flex items-center gap-1.5" data-testid="twofa-paused-status">
              <PauseCircle size={16} weight="duotone" /> Two-factor is enrolled but <b className="mx-0.5">PAUSED</b> — logins skip the code for now.
            </div>
          ) : (
            <div className="text-sm text-[#2F6B3A] flex items-center gap-1.5" data-testid="twofa-on-status">
              <CheckCircle size={16} weight="duotone" /> Two-factor is ON for your account.
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <button onClick={() => togglePause(!paused)} disabled={busy} data-testid="twofa-toggle-pause"
              className="text-xs px-3 py-2 rounded border hairline text-[#4A5D4E] hover:bg-[#F0F1EE] flex items-center gap-1.5">
              {paused
                ? (<><ShieldCheck size={13} weight="duotone" /> Resume two-factor</>)
                : (<><PauseCircle size={13} weight="duotone" /> Pause (for testing)</>)}
            </button>
            {!showDisarm && (
              <button onClick={() => setShowDisarm(true)} data-testid="twofa-disable-init"
                className="text-xs px-3 py-2 rounded border hairline text-[#C16E5A] hover:bg-[#FDF3F0]">
                Turn off completely
              </button>
            )}
          </div>

          {!paused && (
            <p className="text-[11px] text-[#6B6A68] flex items-start gap-1">
              <Warning size={12} weight="duotone" className="mt-0.5 shrink-0" />
              Pause keeps your setup but skips the code at login — only do this while testing, then resume.
            </p>
          )}

          {showDisarm && (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">Enter a current code to confirm</span>
                <input value={disarmCode} onChange={(e) => setDisarmCode(e.target.value)}
                  data-testid="twofa-disable-code" placeholder="123 456"
                  className="px-3 py-2 rounded border hairline text-sm w-40 tracking-widest text-center" />
              </label>
              <button onClick={disable} disabled={busy || !disarmCode.trim()} data-testid="twofa-disable-confirm"
                className="text-xs px-3 py-2 rounded bg-[#C16E5A] text-white hover:bg-[#A35947] disabled:opacity-50">
                {busy ? "…" : "Confirm off"}
              </button>
              <button onClick={() => { setShowDisarm(false); setDisarmCode(""); }}
                className="text-xs px-3 py-2 rounded border hairline text-[#6B6A68]">Cancel</button>
            </div>
          )}
        </div>
      )}

      {/* OFF state — not yet in setup */}
      {!enabled && !setup && (
        <button onClick={beginSetup} disabled={busy} data-testid="twofa-enable-init"
          className="text-xs px-3 py-2 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
          <ShieldCheck size={14} weight="duotone" /> {busy ? "Starting…" : "Enable two-factor"}
        </button>
      )}

      {/* OFF state — in setup (QR + code) */}
      {!enabled && setup && (
        <div className="space-y-3" data-testid="twofa-setup">
          <p className="text-sm text-[#6B6A68]">1. Scan this with Google Authenticator, Authy, or 1Password:</p>
          <img src={setup.qr_data_url} alt="2FA QR code" width={168} height={168}
            className="border hairline rounded bg-white p-2" data-testid="twofa-qr" />
          <div className="text-xs text-[#6B6A68] flex items-center gap-1.5 flex-wrap">
            <span>Can't scan? Enter this key manually:</span>
            <code className="bg-[#F0F1EE] px-1.5 py-0.5 rounded font-mono text-[11px]" data-testid="twofa-secret">{setup.secret}</code>
            <button type="button" data-testid="twofa-copy-secret"
              onClick={() => { navigator.clipboard?.writeText(setup.secret); toast.success("Key copied."); }}
              className="inline-flex items-center gap-1 text-[#4A5D4E] hover:underline">
              <Copy size={11} /> copy
            </button>
          </div>
          <label className="flex flex-col gap-1 max-w-xs">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#6B6A68]">2. Enter the 6-digit code it shows</span>
            <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-setup-code"
              placeholder="123 456"
              className="px-3 py-2 rounded border hairline text-sm tracking-widest text-center" />
          </label>
          <div className="flex gap-2">
            <button onClick={confirmEnable} disabled={busy || !code.trim()} data-testid="twofa-setup-confirm"
              className="text-xs px-3 py-2 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50">
              {busy ? "Verifying…" : "Verify & turn on"}
            </button>
            <button onClick={() => { setSetup(null); setCode(""); }}
              className="text-xs px-3 py-2 rounded border hairline text-[#6B6A68]">Cancel</button>
          </div>
        </div>
      )}
    </Card>
  );
}
